/**
 * CobrancaDashboard — restricted dashboard for the "cobranca" user.
 *
 * Sees ONLY pending items across 3 verticals:
 *   1. Recibos de Férias (vacation-notices)
 *   2. Avisos de Férias  (vacation-advances)
 *   3. Kit Admissional   (admission-kits)
 *
 * For each batch/employee that still has pending signatures, the panel
 * offers the throttled WhatsApp bulk-reminder (BulkRemindPanel). All
 * admin-only actions (upload / delete / edit / issue certificates /
 * storage panel) are intentionally absent — this dashboard is a
 * cobrança-only cockpit.
 *
 * Endpoint permissions:
 *   - `/admin/vacation-notices*` and `/admin/vacation-advances*` are
 *     currently un-authenticated on the server (kept as-is on purpose
 *     — see server.py). We rely on route protection at the frontend +
 *     JWT presence on the API calls.
 *   - `/admin/admission-kits/by-employee` and `by-posto` now accept
 *     the cobranca JWT via `_require_admin_or_cobranca` in the backend.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { BulkRemindPanel } from "../components/BulkRemindPanel";
import SignaturesSummaryPie from "../components/SignaturesSummaryPie";
import {
  LogOut, Megaphone, ReceiptText, Package, ChevronDown, ChevronUp,
  Loader2, Search, AlertCircle, PenLine, Download,
} from "lucide-react";

// ---------- Helpers -----------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch {
    return "—";
  }
};

// ---------- Batch row (used by Recibos + Avisos) ------------------------
function BatchRow({
  batch, // { id, total, signed, pending, created_at, extra? }
  loadDetail, // async () => list of recipients
  bulkPreviewUrl,
  bulkFireUrl,
  docLabel,
  colorClass,
  onDone,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingList, setPendingList] = useState([]);

  const refreshPendingList = async () => {
    setLoading(true);
    try {
      const rows = await loadDetail();
      setPendingList(rows);
    } catch {
      toast.error("Erro ao carregar pendentes");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && pendingList.length === 0) {
      await refreshPendingList();
    }
  };

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition text-left"
        data-testid={`cobranca-batch-toggle-${batch.id}`}
      >
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 text-sm truncate">
            Lote de {fmtDate(batch.created_at)}
          </p>
          <p className="text-[11px] text-slate-500 font-mono">
            #{batch.id.slice(0, 8)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className={`${colorClass} shrink-0`}>
            {batch.pending} pendente{batch.pending === 1 ? "" : "s"}
          </Badge>
          <span className="text-[11px] text-slate-500 hidden sm:inline">
            {batch.signed}/{batch.total} assinado(s)
          </span>
          {open
            ? <ChevronUp className="w-4 h-4 text-slate-400" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {open && (
        <CardContent className="pt-3 pb-4 border-t border-slate-100">
          <BulkRemindPanel
            previewUrl={bulkPreviewUrl}
            fireUrl={bulkFireUrl}
            hasPending={batch.pending > 0}
            docLabel={docLabel}
            onDone={() => {
              refreshPendingList();
              if (onDone) onDone();
            }}
          />
          {loading ? (
            <div className="py-6 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              Carregando pendentes…
            </div>
          ) : pendingList.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">
              Nenhum pendente neste lote.
            </div>
          ) : (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 mt-1">
                Funcionários pendentes ({pendingList.length})
              </p>
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-72 overflow-y-auto">
                {pendingList.map((p) => {
                  const wa = (p.whatsapp || "").trim();
                  const tel = (p.telefone || "").trim();
                  const isWa = !!wa && p.whatsapp_status === "confirmed";
                  const shown = isWa ? wa : tel;
                  return (
                    <li
                      key={p.recipient_id || p.employee_id}
                      className="px-3 py-2 text-sm flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.nome || "—"}</p>
                        <p className="text-[11px] text-slate-500 font-mono">
                          Matrícula {p.matricula || "—"}
                          {p.posto_servico ? ` · ${p.posto_servico}` : ""}
                        </p>
                      </div>
                      {shown ? (
                        <a
                          href={`https://wa.me/${(isWa ? wa : tel).replace(/\D/g, "")}`}
                          target="_blank" rel="noreferrer"
                          className={`text-[11px] font-mono ${
                            isWa ? "text-emerald-700" : "text-slate-500"
                          } hover:underline shrink-0`}
                          data-testid={`cobranca-wa-link-${p.employee_id}`}
                        >
                          {isWa ? "wa: " : "tel: "}{shown}
                        </a>
                      ) : (
                        <span className="text-[11px] text-red-500 shrink-0">
                          sem contato
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ---------- Tab: Recibos de Férias (vacation-notices) -------------------
function RecibosTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/vacation-notices`);
        if (!alive) return;
        setBatches(
          (r.data || [])
            .map((b) => ({
              id: b.id,
              created_at: b.created_at,
              total: b.total || 0,
              signed: b.signed || 0,
              pending: (b.total || 0) - (b.signed || 0),
            }))
            .filter((b) => b.pending > 0),
        );
      } catch {
        if (alive) toast.error("Erro ao carregar Recibos de Férias");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  const loadDetail = (noticeId) => async () => {
    const r = await axios.get(`${API}/admin/vacation-notices/${noticeId}`);
    return (r.data?.recipients || [])
      .filter((x) => x.status !== "signed")
      .map((x) => ({
        recipient_id: x.recipient_id,
        employee_id: x.employee_id,
        nome: x.nome, matricula: x.matricula, posto_servico: x.posto_servico,
        telefone: x.telefone, whatsapp: x.whatsapp,
        whatsapp_status: x.whatsapp_status,
      }));
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Carregando…
      </div>
    );
  }
  if (batches.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-slate-500">
          <AlertCircle className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
          Nenhum lote de Recibos com pendentes.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="cobranca-recibos-list">
      {batches.map((b) => (
        <BatchRow
          key={b.id}
          batch={b}
          loadDetail={loadDetail(b.id)}
          bulkPreviewUrl={`/admin/vacation-notices/${b.id}/bulk-remind/preview`}
          bulkFireUrl={`/admin/vacation-notices/${b.id}/bulk-remind`}
          docLabel="Recibo de Férias pendente"
          colorClass="bg-amber-500"
          onDone={() => setRefreshKey((k) => k + 1)}
        />
      ))}
    </div>
  );
}

// ---------- Tab: Avisos de Férias (vacation-advances) -------------------
function AvisosFeriasTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/vacation-advances`);
        if (!alive) return;
        setBatches(
          (r.data || [])
            .map((b) => ({
              id: b.id,
              created_at: b.created_at,
              total: b.count || 0,
              signed: b.signed_count || 0,
              pending: (b.count || 0) - (b.signed_count || 0),
            }))
            .filter((b) => b.pending > 0),
        );
      } catch {
        if (alive) toast.error("Erro ao carregar Avisos de Férias");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  const loadDetail = (noticeId) => async () => {
    const r = await axios.get(`${API}/admin/vacation-advances/${noticeId}`);
    return (r.data?.items || [])
      .filter((x) => x.status !== "signed")
      .map((x) => ({
        recipient_id: x._recipient_id || x.employee_id,
        employee_id: x.employee_id,
        nome: x.employee_nome,
        matricula: x.employee_matricula,
        posto_servico: x.employee_posto,
        telefone: x.employee_telefone,
        whatsapp: x.employee_whatsapp,
        whatsapp_status: x.employee_whatsapp_status,
      }));
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Carregando…
      </div>
    );
  }
  if (batches.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-slate-500">
          <AlertCircle className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
          Nenhum lote de Avisos com pendentes.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="cobranca-avisos-list">
      {batches.map((b) => (
        <BatchRow
          key={b.id}
          batch={b}
          loadDetail={loadDetail(b.id)}
          bulkPreviewUrl={`/admin/vacation-advances/${b.id}/bulk-remind/preview`}
          bulkFireUrl={`/admin/vacation-advances/${b.id}/bulk-remind`}
          docLabel="Aviso de Férias pendente"
          colorClass="bg-orange-500"
          onDone={() => setRefreshKey((k) => k + 1)}
        />
      ))}
    </div>
  );
}

// ---------- Tab: Kit Admissional -----------------------------------------
function KitAdmissionalTab({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/admission-kits/by-employee`,
          {
            params: debounced ? { search: debounced } : {},
            headers: { Authorization: `Bearer ${adminToken}` },
            timeout: 60000,
          },
        );
        if (!alive) return;
        // Filter to only employees with pending kit docs.
        const pendingOnly = (r.data?.employees || []).filter(
          (e) => (e.kit?.pending || 0) > 0,
        );
        setRows(pendingOnly);
      } catch {
        if (alive) toast.error("Erro ao carregar Kit Admissional");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [debounced, refreshKey, adminToken]);

  const totalPending = useMemo(
    () => rows.reduce((acc, r) => acc + (r.kit?.pending || 0), 0),
    [rows],
  );

  return (
    <div className="space-y-3">
      <BulkRemindPanel
        previewUrl="/admin/admission-kits/bulk-remind/preview"
        fireUrl="/admin/admission-kits/bulk-remind"
        hasPending={totalPending > 0}
        docLabel="Kit Admissional pendente"
        onDone={() => setRefreshKey((k) => k + 1)}
      />
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nome ou matrícula…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="cobranca-kit-search"
          />
        </div>
        <Badge variant="outline">
          {rows.length} funcionário(s) · {totalPending} doc(s) pendente(s)
        </Badge>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
          Carregando…
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            Nenhum Kit Admissional pendente.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul
              className="divide-y divide-slate-100 max-h-[540px] overflow-y-auto"
              data-testid="cobranca-kit-list"
            >
              {rows.map((r) => {
                const wa = (r.whatsapp || "").trim();
                const tel = (r.telefone || "").trim();
                const isWa = !!wa && r.whatsapp_status === "confirmed";
                const shown = isWa ? wa : tel;
                return (
                  <li
                    key={r.id}
                    className="px-4 py-3 text-sm flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{r.nome || "—"}</p>
                      <p className="text-[11px] text-slate-500 font-mono">
                        Matrícula {r.matricula || "—"}
                        {r.posto_servico ? ` · ${r.posto_servico}` : ""}
                      </p>
                    </div>
                    <Badge className="bg-amber-500 shrink-0">
                      {r.kit?.pending || 0} pendente
                      {(r.kit?.pending || 0) === 1 ? "" : "s"}
                    </Badge>
                    {shown ? (
                      <a
                        href={`https://wa.me/${(isWa ? wa : tel).replace(/\D/g, "")}`}
                        target="_blank" rel="noreferrer"
                        className={`text-[11px] font-mono ${
                          isWa ? "text-emerald-700" : "text-slate-500"
                        } hover:underline shrink-0`}
                      >
                        {isWa ? "wa: " : "tel: "}{shown}
                      </a>
                    ) : (
                      <span className="text-[11px] text-red-500 shrink-0">
                        sem contato
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------- Tab: Rúbricas (signature/rúbrica pendente) ------------------
function RubricasTab({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/signatures/pending`,
          {
            headers: { Authorization: `Bearer ${adminToken}` },
            timeout: 30000,
          },
        );
        if (alive) setRows(r.data?.items || []);
      } catch {
        if (alive) toast.error("Erro ao carregar rúbricas pendentes");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [adminToken]);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.nome || "").toUpperCase().includes(q)
      || (r.matricula || "").toUpperCase().includes(q)
      || (r.posto_servico || "").toUpperCase().includes(q),
    );
  }, [rows, search]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await axios.get(
        `${API}/admin/signatures/pending/export`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob",
          timeout: 60000,
        },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `AVI-Rubricas-Pendentes-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `${r.headers["x-pending-count"] || rows.length} pendente(s) exportado(s).`,
      );
    } catch {
      toast.error("Falha ao gerar PDF.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
        <PenLine className="w-4 h-4 shrink-0" />
        <span>
          Funcionários que ainda <strong>não registraram sua rúbrica</strong>{" "}
          no portal AVI. A rúbrica é solicitada no primeiro acesso — oriente
          o funcionário pelo telefone/WhatsApp.
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nome, matrícula ou posto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="cobranca-rubricas-search"
          />
        </div>
        <Badge variant="outline">
          {filtered.length} de {rows.length} pendente(s)
        </Badge>
        <Button
          size="sm"
          variant="outline"
          onClick={exportPdf}
          disabled={exporting || rows.length === 0}
          className="border-amber-300 text-amber-800 hover:bg-amber-50"
          data-testid="cobranca-rubricas-export"
        >
          {exporting
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Gerando…</>
            : <><Download className="w-3.5 h-3.5 mr-1.5" />Exportar PDF</>}
        </Button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
          Carregando…
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            {rows.length === 0
              ? "Todos os funcionários já registraram sua rúbrica."
              : "Nenhum funcionário encontrado com esse filtro."}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul
              className="divide-y divide-slate-100 max-h-[540px] overflow-y-auto"
              data-testid="cobranca-rubricas-list"
            >
              {filtered.map((r) => {
                const phone = (r.telefone || "").replace(/\D/g, "");
                return (
                  <li
                    key={r.id}
                    className="px-4 py-3 text-sm flex items-center gap-3"
                    data-testid={`cobranca-rubricas-item-${r.matricula}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{r.nome || "—"}</p>
                      <p className="text-[11px] text-slate-500">
                        <span className="font-mono">Matr. {r.matricula || "—"}</span>
                        {r.posto_servico ? (
                          <span> · <span className="text-slate-600">{r.posto_servico}</span></span>
                        ) : (
                          <span className="text-slate-400"> · sem posto</span>
                        )}
                      </p>
                    </div>
                    <Badge className="bg-amber-500 shrink-0">
                      sem rúbrica
                    </Badge>
                    {phone ? (
                      <a
                        href={`https://wa.me/${phone}`}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] font-mono text-emerald-700 hover:underline shrink-0"
                        title="Abrir conversa no WhatsApp"
                      >
                        {r.telefone_fmt || phone}
                      </a>
                    ) : (
                      <span className="text-[11px] text-red-500 shrink-0">
                        sem contato
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ---------- Dashboard shell ---------------------------------------------
export default function CobrancaDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 leading-tight">
                AVI Segurança — Cobrança
              </p>
              <p className="text-xs text-slate-500">
                {user?.name || "Usuário de Cobrança"}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            data-testid="cobranca-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1" />
            Sair
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            <strong>Painel de Cobrança.</strong> Apenas itens pendentes são
            exibidos. Cobranças por WhatsApp respeitam throttling anti-ban
            (15s entre envios, cap 40/dia, horário 09h–17h · seg–sex).
          </div>

          {/* Global signatures rollup — donut chart with per-category breakdown */}
          <SignaturesSummaryPie />

          <Tabs defaultValue="recibos" className="w-full">
            <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4">
              <TabsTrigger value="recibos" data-testid="cobranca-tab-recibos">
                <ReceiptText className="w-4 h-4 mr-1.5" />
                Recibos
              </TabsTrigger>
              <TabsTrigger value="avisos" data-testid="cobranca-tab-avisos">
                <Megaphone className="w-4 h-4 mr-1.5" />
                Avisos de Férias
              </TabsTrigger>
              <TabsTrigger value="kit" data-testid="cobranca-tab-kit">
                <Package className="w-4 h-4 mr-1.5" />
                Kit Admissional
              </TabsTrigger>
              <TabsTrigger value="rubricas" data-testid="cobranca-tab-rubricas">
                <PenLine className="w-4 h-4 mr-1.5" />
                Rúbricas
              </TabsTrigger>
            </TabsList>

            <TabsContent value="recibos" className="mt-4">
              <RecibosTab />
            </TabsContent>
            <TabsContent value="avisos" className="mt-4">
              <AvisosFeriasTab />
            </TabsContent>
            <TabsContent value="kit" className="mt-4">
              <KitAdmissionalTab adminToken={token} />
            </TabsContent>
            <TabsContent value="rubricas" className="mt-4">
              <RubricasTab adminToken={token} />
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
}
